import i18n from 'i18next';
import { GUEST_DASHBOARD_TABS, ManageSmartAssistantTabSlug } from 'shared/constants/route-links';
import { Size } from 'shared/interfaces/Size';
import { SmartAssistantColorTheme, SmartAssistantType, WhiteLabelIntegration } from 'shared/interfaces/SmartAssistant';
import { InfoType } from 'shared/interfaces/tools/CollectLeadsInfo';
import { CustomApiRequestMethod } from 'shared/interfaces/tools/CustomApiRequest';
import { ToolTrigger } from 'shared/interfaces/tools/Tool';

export const DEFAULT_SMART_ASSISTANT_SIZE = {
  width: 450,
  height: 600
} as Size;

export const MIN_SMART_ASSISTANT_SIZE = {
  width: 350,
  height: 460
} as Size;

export const SMART_ASSISTANT_GRADIENT_COLORS_COUNT = 4;
export const DEFAULT_USER_MESSAGE_COLOR = '#E0EEFA';
export const DEFAULT_ASSISTANT_MESSAGE_COLOR = '#F6F7F8';
export const DEFAULT_SMART_ASSISTANT_CONVERSATION_BACKGROUND_COLOR = '#FFFFFF';
export const DEFAULT_SMART_ASSISTANT_SECONDARY_BACKGROUND_COLOR = '#F6F7F8';
export const DEFAULT_SMART_ASSISTANT_GRADIENT_COLOR = ['#FFE3D2FF', '#E0986DFF', '#56F2B14D', '#5275F2B3'];

export const DEFAULT_SMART_ASSISTANT_COLOR_THEME: SmartAssistantColorTheme = {
  assistantMessageColor: DEFAULT_ASSISTANT_MESSAGE_COLOR,
  userMessageColor: DEFAULT_USER_MESSAGE_COLOR,
  conversationBackgroundColor: DEFAULT_SMART_ASSISTANT_CONVERSATION_BACKGROUND_COLOR,
  secondaryBackgroundColor: DEFAULT_SMART_ASSISTANT_SECONDARY_BACKGROUND_COLOR,
  gradientColors: DEFAULT_SMART_ASSISTANT_GRADIENT_COLOR
};

export const SMART_ASSISTANT_COLOR_THEMES: SmartAssistantColorTheme[] = [
  DEFAULT_SMART_ASSISTANT_COLOR_THEME,
  {
    assistantMessageColor: '#252525',
    userMessageColor: '#D51317',
    conversationBackgroundColor: '#141414',
    secondaryBackgroundColor: '#252525',
    gradientColors: ['#D51317', '#D51317', '#D51317', '#D51317']
  },
  {
    assistantMessageColor: '#F6F7F8',
    userMessageColor: '#DFF7D2',
    conversationBackgroundColor: '#FFFFFF',
    secondaryBackgroundColor: '#EEF8E8',
    gradientColors: ['#B3E299FF', '#5EF2564D', '#5EF2564D', '#D2FFFFFF']
  },
  {
    assistantMessageColor: '#F8EAEA',
    userMessageColor: '#FFB7B7',
    conversationBackgroundColor: '#FFF7F7',
    secondaryBackgroundColor: '#F8EAEA',
    gradientColors: ['#FD9A98FF', '#F28C52B3', '#F2D8564D', '#E6FFD2FF']
  },
  {
    assistantMessageColor: '#F6F7F8',
    userMessageColor: '#F1DFFF',
    conversationBackgroundColor: '#FFFFFF',
    secondaryBackgroundColor: '#F6F7F8',
    gradientColors: ['#FD98D1FF', '#C752F2B3', '#D2DFFFFF', '#7B56F24D']
  }
];

export const SMART_ASSISTANT_TOKEN_FACTORS = {
  GPT_4_O: 16,
  GPT_4_O_MINI: 1,
  CLAUDE_3_5_SONNET: 20,
  CLAUDE_3_7_SONNET: 20,
  CLAUDE_3_5_HAIKU: 6,
  GOOGLE_GEMINI_PRO_1_5: 8,
  GOOGLE_GEMINI_FLASH_1_5: 1,
  GOOGLE_GEMINI_FLASH_2_0: 1
};

export const SMART_ASSISTANT_HORIZONTAL_PADDING = 10;
export const SMART_ASSISTANT_Y_PADDING = 5;
export const SMART_ASSITANT_ICON_SIZE = 20;
export const DELAY_IN_MILLISECONDS_BEFORE_SCROLLING_TO_BOTTOM = 50;

export const MAX_SMART_ASSISTANT_NAME_LENGTH = 30;

export const SMART_ASSISTANT_TIMEOUT = 300_000;

export const MY_SMART_ASSISTANTS_FILTER = i18n.t('ORGANISATION.TEAMS.SMART_ASSISTANT_TEAM_FILTER.CREATED_BY_ME');

export const NULL_WHITE_LABEL_INTEGRATION = {
  whiteLabelDomain: '',
  domain: '',
  subDomain: ''
} as WhiteLabelIntegration;

export const DEFAULT_LEAD_COLLECTION_TOOL_CONFIG_FORM_VALUES = {
  trigger: ToolTrigger.FIRST_SMART_ASSISTANT_MESSAGE,
  infoTypesToCollect: [InfoType.EMAIL, InfoType.NAME, InfoType.PHONE] as InfoType[],
  emailLabel: i18n.t('SMART_ASSISTANT.TOOLS.LEAD_COLLECTION.LABELS.EMAIL'),
  nameLabel: i18n.t('SMART_ASSISTANT.TOOLS.LEAD_COLLECTION.LABELS.NAME'),
  phoneLabel: i18n.t('SMART_ASSISTANT.TOOLS.LEAD_COLLECTION.LABELS.PHONE'),
  submitButtonLabel: i18n.t('SMART_ASSISTANT.TOOLS.LEAD_COLLECTION.LABELS.SUBMIT'),
  collectionAcknowledgementMessage: i18n.t('SMART_ASSISTANT.TOOLS.LEAD_COLLECTION.ACKNOWLEDGEMENT.DEFAULT_VALUE'),
  title: i18n.t('SMART_ASSISTANT.TOOLS.LEAD_COLLECTION.FORM_TITLE.DEFAULT_VALUE')
};

export const DEFAULT_CALENDLY_TOOL_CONFIG_FORM_VALUES = {
  trigger: ToolTrigger.BUTTON,
  eventUrl: '',
  ctaMessage: i18n.t('SMART_ASSISTANT.TOOLS.CALENDLY.CTA_MESSAGE.DEFAULT_VALUE')
};

export const DEFAULT_EMAIL_HANDOFF_TOOL_CONFIG_FORM_VALUES = {
  infoTypesToCollect: [InfoType.EMAIL, InfoType.NAME, InfoType.PHONE, InfoType.REASON] as InfoType[],
  emailLabel: i18n.t('SMART_ASSISTANT.TOOLS.EMAIL_HANDOFF.LABELS.EMAIL'),
  nameLabel: i18n.t('SMART_ASSISTANT.TOOLS.EMAIL_HANDOFF.LABELS.NAME'),
  phoneLabel: i18n.t('SMART_ASSISTANT.TOOLS.EMAIL_HANDOFF.LABELS.PHONE'),
  reasonLabel: i18n.t('SMART_ASSISTANT.TOOLS.EMAIL_HANDOFF.LABELS.REASON'),
  title: i18n.t('SMART_ASSISTANT.TOOLS.EMAIL_HANDOFF.FORM_TITLE.DEFAULT_VALUE'),
  emailToHandoff: ''
};

export const DEFAULT_CUSTOM_API_REQUEST_TOOL_CONFIG_FORM_VALUES = {
  toolName: '',
  description: '',
  parametersJsonSchema: '{}',
  responseHandlingConfiguration: {
    successInstruction: '',
    errorInstruction: ''
  },
  requestConfiguration: {
    method: CustomApiRequestMethod.GET,
    urlTemplate: '',
    headersTemplate: {},
    queryParametersTemplate: {},
    bodyTemplate: ''
  }
};

export enum BehaviorPromptAction {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE'
}

export interface GenerateBehaviorPromptDto {
  currentPrompt: string;
  instruction: string;
  action: BehaviorPromptAction;
  smartAssistantType: SmartAssistantType;
}

export const SMART_ASSISTANT_MANAGE_TABS_BY_TYPE: Record<SmartAssistantType, ManageSmartAssistantTabSlug[]> = {
  [SmartAssistantType.BASIC]: [
    ManageSmartAssistantTabSlug.CUSTOMIZATION,
    ManageSmartAssistantTabSlug.CONFIGURATION,
    ManageSmartAssistantTabSlug.RESOURCES,
    ManageSmartAssistantTabSlug.TOOLS,
    ManageSmartAssistantTabSlug.CONVERSATIONS,
    ManageSmartAssistantTabSlug.MEMORIES,
    ManageSmartAssistantTabSlug.EVALUATIONS,
    ManageSmartAssistantTabSlug.ADVANCED
  ],
  [SmartAssistantType.WEBSITE]: [
    ManageSmartAssistantTabSlug.CUSTOMIZATION,
    ManageSmartAssistantTabSlug.CONFIGURATION,
    ManageSmartAssistantTabSlug.RESOURCES,
    ManageSmartAssistantTabSlug.WEBSITE_INTEGRATION,
    ManageSmartAssistantTabSlug.TOOLS,
    ManageSmartAssistantTabSlug.LEADS,
    ManageSmartAssistantTabSlug.CONVERSATIONS,
    ManageSmartAssistantTabSlug.MEMORIES,
    ManageSmartAssistantTabSlug.EVALUATIONS,
    ManageSmartAssistantTabSlug.ADVANCED
  ],
  [SmartAssistantType.SHOPIFY]: [
    ManageSmartAssistantTabSlug.STORE,
    ManageSmartAssistantTabSlug.CUSTOMIZATION,
    ManageSmartAssistantTabSlug.CONFIGURATION,
    ManageSmartAssistantTabSlug.RESOURCES,
    ManageSmartAssistantTabSlug.TOOLS,
    ManageSmartAssistantTabSlug.LEADS,
    ManageSmartAssistantTabSlug.CONVERSATIONS,
    ManageSmartAssistantTabSlug.MEMORIES,
    ManageSmartAssistantTabSlug.EVALUATIONS,
    ManageSmartAssistantTabSlug.ADVANCED
  ],
  [SmartAssistantType.PHONE]: [
    ManageSmartAssistantTabSlug.PHONE_AGENT_BASICS,
    ManageSmartAssistantTabSlug.CONFIGURATION,
    ManageSmartAssistantTabSlug.RESOURCES,
    ManageSmartAssistantTabSlug.TOOLS,
    ManageSmartAssistantTabSlug.TELEPHONY,
    ManageSmartAssistantTabSlug.VOICE_CALLS,
    ManageSmartAssistantTabSlug.MEMORIES
  ],
  [SmartAssistantType.DISCORD]: [
    ManageSmartAssistantTabSlug.MESSAGING_BASICS,
    ManageSmartAssistantTabSlug.CONFIGURATION,
    ManageSmartAssistantTabSlug.RESOURCES,
    ManageSmartAssistantTabSlug.CONVERSATIONS,
    ManageSmartAssistantTabSlug.MEMORIES,
    ManageSmartAssistantTabSlug.EVALUATIONS
  ],
  [SmartAssistantType.WHATSAPP]: [
    ManageSmartAssistantTabSlug.MESSAGING_BASICS,
    ManageSmartAssistantTabSlug.CONFIGURATION,
    ManageSmartAssistantTabSlug.RESOURCES,
    ManageSmartAssistantTabSlug.TOOLS,
    ManageSmartAssistantTabSlug.CONVERSATIONS,
    ManageSmartAssistantTabSlug.MEMORIES,
    ManageSmartAssistantTabSlug.EVALUATIONS,
    ManageSmartAssistantTabSlug.ADVANCED
  ],
  [SmartAssistantType.INSTAGRAM]: [
    ManageSmartAssistantTabSlug.MESSAGING_BASICS,
    ManageSmartAssistantTabSlug.CONFIGURATION,
    ManageSmartAssistantTabSlug.RESOURCES,
    ManageSmartAssistantTabSlug.TOOLS,
    ManageSmartAssistantTabSlug.CONVERSATIONS,
    ManageSmartAssistantTabSlug.MEMORIES,
    ManageSmartAssistantTabSlug.EVALUATIONS,
    ManageSmartAssistantTabSlug.ADVANCED
  ],
  [SmartAssistantType.FACEBOOK]: [
    ManageSmartAssistantTabSlug.MESSAGING_BASICS,
    ManageSmartAssistantTabSlug.CONFIGURATION,
    ManageSmartAssistantTabSlug.RESOURCES,
    ManageSmartAssistantTabSlug.TOOLS,
    ManageSmartAssistantTabSlug.CONVERSATIONS,
    ManageSmartAssistantTabSlug.MEMORIES,
    ManageSmartAssistantTabSlug.EVALUATIONS,
    ManageSmartAssistantTabSlug.ADVANCED
  ],
  // The following smart assistant types are not available yet
  [SmartAssistantType.BIGCOMMERCE]: [],
  [SmartAssistantType.WIX]: [],
  [SmartAssistantType.WOOCOMMERCE]: [],
  [SmartAssistantType.SQUARESPACE]: [],
  [SmartAssistantType.SMS]: []
};

export const GUEST_SMART_ASSISTANT_DASHBOARD_TABS_BY_TYPE: Record<SmartAssistantType, string[]> = {
  [SmartAssistantType.BASIC]: [
    GUEST_DASHBOARD_TABS.OVERVIEW,
    GUEST_DASHBOARD_TABS.CONVERSATIONS,
    GUEST_DASHBOARD_TABS.EVALUATIONS
  ],
  [SmartAssistantType.WEBSITE]: [
    GUEST_DASHBOARD_TABS.OVERVIEW,
    GUEST_DASHBOARD_TABS.CONVERSATIONS,
    GUEST_DASHBOARD_TABS.LEADS,
    GUEST_DASHBOARD_TABS.EVALUATIONS
  ],
  [SmartAssistantType.SHOPIFY]: [
    GUEST_DASHBOARD_TABS.OVERVIEW,
    GUEST_DASHBOARD_TABS.CONVERSATIONS,
    GUEST_DASHBOARD_TABS.LEADS,
    GUEST_DASHBOARD_TABS.EVALUATIONS
  ],
  [SmartAssistantType.PHONE]: [GUEST_DASHBOARD_TABS.OVERVIEW, GUEST_DASHBOARD_TABS.VOICE_CALLS],
  [SmartAssistantType.DISCORD]: [
    GUEST_DASHBOARD_TABS.OVERVIEW,
    GUEST_DASHBOARD_TABS.CONVERSATIONS,
    GUEST_DASHBOARD_TABS.EVALUATIONS
  ],
  [SmartAssistantType.WHATSAPP]: [
    GUEST_DASHBOARD_TABS.OVERVIEW,
    GUEST_DASHBOARD_TABS.CONVERSATIONS,
    GUEST_DASHBOARD_TABS.EVALUATIONS
  ],
  [SmartAssistantType.INSTAGRAM]: [
    GUEST_DASHBOARD_TABS.OVERVIEW,
    GUEST_DASHBOARD_TABS.CONVERSATIONS,
    GUEST_DASHBOARD_TABS.EVALUATIONS
  ],
  [SmartAssistantType.FACEBOOK]: [
    GUEST_DASHBOARD_TABS.OVERVIEW,
    GUEST_DASHBOARD_TABS.CONVERSATIONS,
    GUEST_DASHBOARD_TABS.EVALUATIONS
  ],
  [SmartAssistantType.BIGCOMMERCE]: [],
  [SmartAssistantType.WIX]: [],
  [SmartAssistantType.WOOCOMMERCE]: [],
  [SmartAssistantType.SQUARESPACE]: [],
  [SmartAssistantType.SMS]: []
};
